import { Screen, ScreenConfig } from '../core/Screen';
import { CodexChapter, CHAPTERS } from './codex/types';
import { StrandsState, createStrandsState, renderStrands } from './codex/StrandsChapter';
import { ItemsState, createItemsState, renderItems } from './codex/ItemsChapter';
import { UniquesState, createUniquesState, renderUniques } from './codex/UniquesChapter';
import { MasteriesState, createMasteriesState, renderMasteries } from './codex/MasteriesChapter';
import { renderMechanics } from './codex/MechanicsChapter';
import { EffectsState, createEffectsState, renderEffects } from './codex/EffectsChapter';
import { BestiaryState, createBestiaryState, renderBestiary } from './codex/BestiaryChapter';
import { FactionsState, createFactionsState, renderFactions } from './codex/FactionsChapter';
import { AffixesState, createAffixesState, renderAffixes } from './codex/AffixesChapter';
import { SkillsState, createSkillsState, renderSkills } from './codex/SkillsChapter';
import { renderElements } from './codex/ElementsChapter';
import { renderAchievements } from './codex/AchievementsChapter';

export class CodexScreen extends Screen {
  readonly screenConfig: ScreenConfig = {
    id: 'codex',
    name: 'Codex',
    persistent: false,
    needsPhaser: false,
  };

  constructor() {
    super({ rootId: 'codex-screen-root' });
  }

  private activeChapter: CodexChapter = 'strands';

  // Per-chapter state
  private strandsState: StrandsState = createStrandsState();
  private itemsState: ItemsState = createItemsState();
  private uniquesState: UniquesState = createUniquesState();
  private masteriesState: MasteriesState = createMasteriesState();
  private effectsState: EffectsState = createEffectsState();
  private bestiaryState: BestiaryState = createBestiaryState();
  private affixesState: AffixesState = createAffixesState();
  private skillsState: SkillsState = createSkillsState();
  private factionsState: FactionsState = createFactionsState();

  createDOM(container: HTMLElement): void {
    const root = document.createElement('div');
    root.id = 'codex-screen-root';
    root.innerHTML = `<div class="codex-screen">
      <nav class="codex-nav">${CHAPTERS.map(ch =>
        `<button class="codex-nav-btn${ch.id === this.activeChapter ? ' active' : ''}" data-chapter="${ch.id}">
          <span>${ch.icon}</span>
          <span class="codex-nav-label">${ch.label}</span>
        </button>`
      ).join('')}</nav>
      <div class="codex-content" id="codex-content"></div>
    </div>`;
    container.appendChild(root);
  }

  protected cacheElements(): void {}
  protected bindEvents(): void {
    this.rootEl?.querySelectorAll('.codex-nav-btn').forEach(btn => {
      btn.addEventListener('click', () => {
        const chapter = (btn as HTMLElement).dataset.chapter as CodexChapter;
        if (chapter) this.switchChapter(chapter);
      });
    });
  }
  protected setupSubscriptions(): void {}
  protected onRender(): void {}

  onEnter(): void {
    this.renderChapter();
  }

  private switchChapter(chapter: CodexChapter): void {
    this.activeChapter = chapter;
    // Update nav active state
    this.rootEl?.querySelectorAll('.codex-nav-btn').forEach(btn => {
      btn.classList.toggle('active', (btn as HTMLElement).dataset.chapter === chapter);
    });
    this.renderChapter();
    const content = this.rootEl?.querySelector('#codex-content') as HTMLElement | null;
    if (content) content.scrollTop = 0;
  }

  private renderChapter(): void {
    const content = this.rootEl?.querySelector('#codex-content') as HTMLElement;
    if (!content) return;

    switch (this.activeChapter) {
      case 'strands': renderStrands(content, this.strandsState); break;
      case 'skills': renderSkills(content, this.skillsState); break;
      case 'items': renderItems(content, this.itemsState, (ch) => this.switchChapter(ch), (base) => { this.affixesState.baseFilter = base; }); break;
      case 'uniques': renderUniques(content, this.uniquesState); break;
      case 'masteries': renderMasteries(content, this.masteriesState); break;
      case 'mechanics': renderMechanics(content); break;
      case 'effects': renderEffects(content, this.effectsState); break;
      case 'bestiary': renderBestiary(content, this.bestiaryState); break;
      case 'factions': renderFactions(content, this.factionsState); break;
      case 'affixes': renderAffixes(content, this.affixesState); break;
      case 'elements': renderElements(content); break;
      case 'achievements': renderAchievements(content); break;
    }
  }
}
